import upperFirst from 'lodash/upperFirst';
import React, { useMemo } from 'react';
import { COMPLAINT_SOURCES } from '@/complaints/complaints-constants';
import styled from 'styled-components';
import { TComplaintData } from './types';

const NOT_PROVIDED = 'Not provided';

const Data = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Value = styled.div`
  font-weight: bold;
`;

function Detail({ label, value = NOT_PROVIDED, dataCy }) {
  return (
    <Data data-cy={dataCy}>
      <div>{label}</div>
      <Value>{value}</Value>
    </Data>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const DetailsHeader = styled.div`
  margin-bottom: 15px;
  padding-bottom: 5px;
  border-bottom: 1px solid black;
`;

const Columns = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 30px;
`;

const Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

type TComplaintReportProps = {
  complaintData: TComplaintData;
};

export function ComplaintReport({ complaintData }: TComplaintReportProps) {
  const reporterInfo = useMemo(() => {
    const { contactFirstName, contactLastName } = complaintData;

    const providedGivenName = Boolean(contactFirstName);
    const providedSurname = Boolean(contactLastName);

    if (providedGivenName && providedSurname) {
      return {
        label: 'Complainant name',
        value: `${contactFirstName} ${contactLastName}`,
      };
    }

    if (providedGivenName) {
      return { label: 'Complainant given name', value: contactFirstName };
    }

    if (providedSurname) {
      return { label: 'Complainant surname', value: contactLastName };
    }

    return { label: 'Complainant name' }; // No value as we want to display the default in "Detail" component
  }, [complaintData]);

  const isValidListingURL = useMemo(() => {
    try {
      return Boolean(new URL(complaintData.listingUrl));
    } catch {
      return false;
    }
  }, [complaintData.listingUrl]);

  return (
    <Container>
      <DetailsHeader>Complaint Details</DetailsHeader>

      <Columns>
        <Column>
          <Detail
            {...reporterInfo}
            dataCy="complaint-details-complaint-reporter-info"
          />
          <Detail
            label="Phone number"
            value={complaintData.contactPhone}
            dataCy="complaint-details-complaint-complainant-phone"
          />
          <Detail
            label="Email"
            value={complaintData.contactEmail}
            dataCy="complaint-details-complaint-complainant-email"
          />

          <Detail
            label="Property Owner Name"
            value={complaintData.parcelOwnerName || 'Unknown'}
            dataCy="complaint-details-complaint-property-owner-name"
          />

          <Detail
            label="Listing URL"
            value={
              isValidListingURL ? (
                <a target="_blank" href={complaintData.listingUrl}>
                  {new URL(complaintData.listingUrl).hostname}
                </a>
              ) : (
                'Unknown'
              )
            }
            dataCy="complaint-details-complaint-listing-url"
          />
        </Column>

        <Column>
          <Detail
            label="Complaint type(s)"
            value={complaintData.complaintTypes.map(upperFirst).join(', ')}
            dataCy="complaint-details-complaint-complaint-type"
          />

          <Detail
            label="Complaint source"
            value={COMPLAINT_SOURCES[complaintData.source]}
            dataCy="complaint-details-complaint-complaint-source"
          />

          <Detail
            label="First complaint?"
            value={complaintData.hasPriorComplaint ? 'No' : 'Yes'}
            dataCy="complaint-details-complaint-is-first-complaint"
          />

          <Detail
            label="Reference no. of prev complaint"
            value={complaintData.priorComplaintId}
            dataCy="complaint-details-complaint-prior-complaint-id"
          />
        </Column>
      </Columns>

      <div style={{ marginTop: '5px' }}>
        {' '}
        {/* Imitate the "gap: 5px;" of "Column" styled component */}
        <div>Description</div>
        <div>{complaintData.description ?? NOT_PROVIDED}</div>
      </div>
    </Container>
  );
}
